import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { filterUserRoleOptions, Roles } from 'common';
import { useRoles } from 'hooks/useRoles';
import React, { useMemo, useState } from 'react';

interface UserRoleFilterProps {
    handleFilterUserRole: (option: { value: string; label: string }) => void;
    userRolesRecords: { value: string; records: number; textColor?: string }[];
}

const UserRoleFilter = (
    { handleFilterUserRole, userRolesRecords }: UserRoleFilterProps
) => {
    const [filterUserRoleSelected, setFilterUserRoleSelected] = useState(filterUserRoleOptions[0]);

    const { isPSPAdminRole, isBUAdminPSP, isBUAdmin } = useRoles();

    const selectedRoleRecord = useMemo(() => userRolesRecords.find((r) => r.value === filterUserRoleSelected.value), [
        filterUserRoleSelected,
        userRolesRecords,
    ]);
    const selectedRecord: number = useMemo(() => selectedRoleRecord?.records ?? 0, [selectedRoleRecord]);
    const selectedTextColor = useMemo(() => selectedRoleRecord?.textColor ?? 'black', [selectedRoleRecord]);

    return (
        <Box>
            <Menu>
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            as={Button}
                            rightIcon={isOpen ? <ChevronUpIcon boxSize={4} /> : <ChevronDownIcon boxSize={4} />}
                            minW="200px"
                            backgroundColor="white"
                            color="black"
                            borderRadius="5px"
                            border="1px solid #E2E8F0"
                            boxShadow="sm"
                            _hover={{ backgroundColor: "white" }}
                            _active={{ backgroundColor: "white" }}
                            _focus={{ backgroundColor: "white" }}
                            data-testid="user-role-filter"
                            textAlign="left"
                        >
                            <Flex w="100%" justify="space-between">
                                <Text>{filterUserRoleSelected.label}</Text>
                                {filterUserRoleSelected.label !== "User Role" && (
                                    <Text textColor={selectedTextColor}>{selectedRecord}</Text>
                                )}
                            </Flex>
                        </MenuButton>

                        <MenuList>
                            {filterUserRoleOptions.filter((option) => {
                                if (isPSPAdminRole)
                                    return option.value !== Roles.BUApprover;
                                if (isBUAdminPSP)
                                    return option.value === Roles.BUApprover;
                                if (isBUAdmin) return true;
                                return true;
                            }).map((baseOption) => {
                                const option = {
                                    ...baseOption,
                                    label: isPSPAdminRole ? baseOption.label.replace("Business", "PSP") : baseOption.label,
                                };

                                const userRoleRecord = userRolesRecords.find((r) => r.value === option.value);
                                const record: number = userRoleRecord?.records ?? 0;
                                const textColor = userRoleRecord?.textColor ?? "black";

                                return (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => {
                                            setFilterUserRoleSelected(option);
                                            handleFilterUserRole(option);
                                        }}
                                        backgroundColor={
                                            filterUserRoleSelected.value === option.value
                                                ? "blue.50"
                                                : undefined
                                        }
                                        _hover={{
                                            backgroundColor: "gray.50",
                                        }}
                                    >
                                        <Flex w="100%" justify="space-between">
                                            <Text>{option.label}</Text>
                                            {option.label !== "User Role" && (
                                                <Text color={textColor}>{record}</Text>
                                            )}
                                        </Flex>
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </>
                )}
            </Menu>
        </Box>
    );
};

export default UserRoleFilter;